// Admin session state. `me` is undefined while checking, null when logged
// out, and the user object when logged in. A patched fetch flips the app
// back to the login screen the moment any API call answers 401, and stamps
// every API request with the ACTIVE COMPANY (multi-tenant scoping) —
// existing pages never need to know.
import { createSignal } from 'solid-js';
import { setMoneyDecimals } from './money.js';
import { navigate } from './router.js';

const [me, setMe] = createSignal(undefined);
const [companies, setCompanies] = createSignal([]);
const [activeCompany, setActiveCompanySignal] = createSignal(
  Number(localStorage.getItem('payroll.company')) || 0);
// Karyawan portal identity — a separate session from the admin `me`.
const [karyawanMe, setKaryawanMe] = createSignal(undefined);
// brandCompany / brandCompanyName = the company whoever last signed in here
// belonged to; persisted so the LOGIN page can show that company's logo + name
// even before authentication ("" name → never signed in → fall back to app name).
const [brandCompany, setBrandCompanySignal] = createSignal(
  Number(localStorage.getItem('payroll.brandCompany')) || 0);
const [brandCompanyName, setBrandCompanyNameSignal] = createSignal(
  localStorage.getItem('payroll.brandCompanyName') || '');
export { me, companies, activeCompany, karyawanMe, brandCompany, brandCompanyName };

export function setActiveCompany(id) {
  try { localStorage.setItem('payroll.company', String(id)); } catch {}
  setActiveCompanySignal(id);
  if (id) setBrandCompany(id, companies().find((c) => c.id === id)?.name);
}

// name is optional — only updated when provided, so callers with just an id
// (e.g. a switch before the list is loaded) never blank out a known name.
export function setBrandCompany(id, name) {
  if (!id) return;
  try {
    localStorage.setItem('payroll.brandCompany', String(id));
    if (name) localStorage.setItem('payroll.brandCompanyName', name);
  } catch {}
  setBrandCompanySignal(id);
  if (name) setBrandCompanyNameSignal(name);
}

function applySession(data) {
  const list = data.companies ?? [];
  setCompanies(list);
  // Active company must be one the user manages; default to the first.
  if (!list.some(c => c.id === activeCompany())) {
    setActiveCompany(list[0]?.id ?? 0);
  }
  const ac = activeCompany(); // remember company id + name for the login page
  if (ac) setBrandCompany(ac, list.find((c) => c.id === ac)?.name);
  setMoneyDecimals(data.moneyDecimals ?? 0);
  setMe(data.user ?? null);
}

export async function checkSession() {
  try {
    const res = await fetch('/api/me');
    if (!res.ok) { setMe(null); return; }
    applySession(await res.json());
  } catch {
    setMe(null);
  }
}

export async function login(username, password) {
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'login gagal');
  await checkSession(); // /api/me carries the company list
}

export async function logout() {
  await fetch('/api/logout', { method: 'POST' }).catch(() => {});
  setMe(null);
  navigate('/admin'); // admins sign back in via the admin login
}

// ── Karyawan self-service portal ─────────────────────────────────────────
export async function checkKaryawanSession() {
  try {
    const res = await fetch('/api/karyawan/me');
    if (!res.ok) { setKaryawanMe(null); return; }
    const data = await res.json();
    setMoneyDecimals(data.moneyDecimals ?? 0);
    if (data.karyawan?.companyId) setBrandCompany(data.karyawan.companyId, data.karyawan.companyName);
    setKaryawanMe(data.karyawan ?? null);
  } catch {
    setKaryawanMe(null);
  }
}

export async function karyawanLogin(nik, password) {
  const res = await fetch('/api/karyawan/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nik, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? 'login gagal');
  setMoneyDecimals(data.moneyDecimals ?? 0);
  if (data.karyawan?.companyId) setBrandCompany(data.karyawan.companyId, data.karyawan.companyName);
  setKaryawanMe(data.karyawan);
}

export async function karyawanLogout() {
  await fetch('/api/karyawan/logout', { method: 'POST' }).catch(() => {});
  setKaryawanMe(null);
  navigate('/'); // back to the default (karyawan) login
}

// Refresh the karyawan identity (e.g. after a password change lifts read-only).
export function refreshKaryawan() { return checkKaryawanSession(); }

// Install once: stamp X-Company-ID on every API call; on 401 (except the
// login attempt itself) the session is gone — show the login screen.
const origFetch = window.fetch.bind(window);
window.fetch = async (input, init) => {
  const url = typeof input === 'string' ? input : input?.url ?? '';
  if (url.startsWith('/api/') && activeCompany()) {
    init = { ...(init ?? {}) };
    init.headers = { ...(init.headers ?? {}), 'X-Company-ID': String(activeCompany()) };
  }
  const res = await origFetch(input, init);
  if (res.status === 401 && url.startsWith('/api/')) {
    if (url.startsWith('/api/karyawan/')) {
      if (!url.endsWith('/login') && !url.endsWith('/me')) setKaryawanMe(null);
    } else if (url !== '/api/login' && url !== '/api/me') {
      setMe(null);
    }
  }
  return res;
};
